/**
 * Admin Feedback Page: list feedback messages, filter by status, open detail and update.
 * Uses shared AccountPicker (same as home groups, cash tickets) for notification recipient.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../../../components/layout/index.ts";
import {
  IconMessage,
  IconChevronRight,
  IconAlertTriangle,
  IconCheck,
} from "../../../components/Icons.tsx";
import { getTelegram, haptic } from "../../../lib/telegram.ts";
import { useAuth } from "../../../lib/auth.tsx";
import {
  graphql,
  getErrorMessage,
  withMinDelay,
  ADMIN_FEEDBACK_MESSAGES_QUERY,
  ADMIN_UPDATE_FEEDBACK_MUTATION,
  ADMIN_FEEDBACK_CONFIG_QUERY,
  ADMIN_SET_FEEDBACK_CONFIG_MUTATION,
  ADMIN_CLEAR_FEEDBACK_CONFIG_MUTATION,
} from "../../../lib/graphql.ts";
import { formatDateBangkok } from "../../../lib/bangkok-time.ts";
import { AccountPicker } from "../home-groups/components/AccountPicker.tsx";
import type { AdminAccountRef } from "../home-groups/types.ts";

interface FeedbackMessageRow {
  id: string;
  accountId: string | null;
  telegramId: string | null;
  authorName: string | null;
  text: string;
  status: string;
  adminNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface FeedbackConfigResult {
  accountId: string;
  account: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    username: string | null;
    telegramId: string;
  } | null;
}

const STATUS_LABELS: Record<string, string> = {
  NEW: "Новое",
  IN_PROGRESS: "В работе",
  RESOLVED: "Решено",
};

export function AdminFeedbackPage() {
  const navigate = useNavigate();
  const tg = getTelegram();
  const { account } = useAuth();
  const hasLoadedOnce = useRef(false);

  const [list, setList] = useState<FeedbackMessageRow[]>([]);
  const [feedbackConfig, setFeedbackConfig] =
    useState<FeedbackConfigResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [statusEdit, setStatusEdit] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingConfig, setIsSavingConfig] = useState(false);

  const selected = selectedId ? list.find((m) => m.id === selectedId) : null;

  const loadList = useCallback(async () => {
    if (!account) return;
    setIsLoading(true);
    setError(null);
    try {
      const [messagesData, configData] = await Promise.all([
        withMinDelay(
          graphql<{ adminFeedbackMessages: FeedbackMessageRow[] }>(
            ADMIN_FEEDBACK_MESSAGES_QUERY,
            {
              filter:
                statusFilter !== null && statusFilter !== ""
                  ? { status: statusFilter }
                  : undefined,
            },
          ),
        ),
        graphql<{ adminFeedbackConfig: FeedbackConfigResult | null }>(
          ADMIN_FEEDBACK_CONFIG_QUERY,
        ),
      ]);
      setList(messagesData.adminFeedbackMessages);
      setFeedbackConfig(configData.adminFeedbackConfig ?? null);
      hasLoadedOnce.current = true;
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [account, statusFilter]);

  const feedbackAccountRef: AdminAccountRef | null = feedbackConfig?.account
    ? {
        id: feedbackConfig.account.id,
        firstName: feedbackConfig.account.firstName,
        lastName: feedbackConfig.account.lastName,
        username: feedbackConfig.account.username,
      }
    : null;

  const handleFeedbackRecipientChange = useCallback(
    async (acc: AdminAccountRef | null) => {
      haptic("light");
      setIsSavingConfig(true);
      setError(null);
      try {
        if (acc) {
          await graphql<{ adminSetFeedbackConfig: FeedbackConfigResult }>(
            ADMIN_SET_FEEDBACK_CONFIG_MUTATION,
            { accountId: acc.id },
          );
        } else {
          await graphql<{ adminClearFeedbackConfig: boolean }>(
            ADMIN_CLEAR_FEEDBACK_CONFIG_MUTATION,
          );
        }
        await loadList();
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setIsSavingConfig(false);
      }
    },
    [loadList],
  );

  useEffect(() => {
    if (account) loadList();
  }, [account, loadList]);

  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => {
      if (selectedId) {
        setSelectedId(null);
      } else if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/admin/content");
      }
    };
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate, selectedId]);

  useEffect(() => {
    if (selected) {
      setAdminNotes(selected.adminNotes ?? "");
      setStatusEdit(selected.status);
    }
  }, [selected?.id, selected?.adminNotes, selected?.status]);

  const handleSave = async () => {
    if (!selectedId) return;
    haptic("light");
    setIsSaving(true);
    setError(null);
    try {
      await graphql<{ adminUpdateFeedback: { id: string } }>(
        ADMIN_UPDATE_FEEDBACK_MUTATION,
        {
          id: selectedId,
          status: statusEdit || undefined,
          adminNotes: adminNotes.trim() || null,
        },
      );
      await loadList();
      setSelectedId(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (iso: string) => {
    try {
      return formatDateBangkok(iso, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  if (selected) {
    return (
      <Layout>
        <div className="pb-6">
          <div className="card overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <p className="text-[12px] font-medium text-slate-400 uppercase tracking-wide mb-1">
                От кого
              </p>
              <p className="text-slate-900 font-medium">
                {selected.authorName || "Гость"}
                {selected.telegramId && (
                  <span className="text-slate-500 text-sm ml-2">
                    @{selected.telegramId}
                  </span>
                )}
              </p>
              <p className="text-slate-500 text-xs mt-1">
                {formatDate(selected.createdAt)}
              </p>
            </div>
            <div className="p-4 border-b border-slate-100">
              <p className="text-[12px] font-medium text-slate-400 uppercase tracking-wide mb-1">
                Сообщение
              </p>
              <p className="text-slate-800 text-sm whitespace-pre-wrap">
                {selected.text}
              </p>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-[12px] font-medium text-slate-400 uppercase tracking-wide mb-1">
                  Статус
                </label>
                <select
                  value={statusEdit}
                  onChange={(e) => setStatusEdit(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
                >
                  {Object.entries(STATUS_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[12px] font-medium text-slate-400 uppercase tracking-wide mb-1">
                  Заметки администратора
                </label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  rows={3}
                  placeholder="Внутренние заметки..."
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 resize-none"
                />
              </div>
              {error && <p className="text-sm text-amber-600">{error}</p>}
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {isSaving ? (
                  "Сохранение…"
                ) : (
                  <>
                    <IconCheck className="w-4 h-4" />
                    Сохранить
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600">
            <IconMessage className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-slate-900">Обратная связь</h1>
            <p className="text-[13px] text-slate-500">
              Сообщения пользователей
            </p>
          </div>
        </div>

        {/* Кому приходят уведомления: тот же AccountPicker, что в домашних группах и выдаче билетов за кэш */}
        <div className="card overflow-hidden mb-4">
          <p className="text-[12px] font-medium text-slate-400 uppercase tracking-wide px-4 pt-4 pb-2">
            Кому приходят уведомления о новой обратной связи
          </p>
          {isLoading ? (
            <div className="px-4 pb-4 text-slate-500 text-sm">Загрузка…</div>
          ) : (
            <div className="px-4 pb-4">
              <AccountPicker
                value={feedbackAccountRef}
                onChange={handleFeedbackRecipientChange}
                placeholder="Выберите получателя"
                label="Получатель"
              />
              {isSavingConfig && (
                <p className="text-slate-500 text-xs mt-2">Сохранение…</p>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
          <button
            type="button"
            onClick={() => {
              haptic("light");
              setStatusFilter(null);
            }}
            className={`flex-shrink-0 px-3 py-2 rounded-lg text-sm font-medium ${
              statusFilter === null
                ? "bg-teal-600 text-white"
                : "bg-slate-100 text-slate-600"
            }`}
          >
            Все
          </button>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => {
                haptic("light");
                setStatusFilter(value);
              }}
              className={`flex-shrink-0 px-3 py-2 rounded-lg text-sm font-medium ${
                statusFilter === value
                  ? "bg-teal-600 text-white"
                  : "bg-slate-100 text-slate-600"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {error && (
          <div className="mb-4 flex items-center gap-2 text-amber-600 text-sm">
            <IconAlertTriangle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="card p-8 text-center text-slate-500 text-sm">
            Загрузка…
          </div>
        ) : list.length === 0 ? (
          <div className="card p-8 text-center text-slate-500 text-sm">
            Нет сообщений
          </div>
        ) : (
          <div className="card overflow-hidden divide-y divide-slate-100">
            {list.map((msg) => (
              <button
                key={msg.id}
                type="button"
                onClick={() => {
                  haptic("light");
                  setSelectedId(msg.id);
                }}
                className="w-full flex items-center gap-3 min-h-[72px] px-4 py-3 active:bg-slate-50 transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <IconMessage className="w-5 h-5 text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 text-[15px] truncate">
                    {msg.authorName || "Гость"}
                  </p>
                  <p className="text-slate-500 text-xs truncate">
                    {msg.text.slice(0, 60)}
                    {msg.text.length > 60 ? "…" : ""}
                  </p>
                  <p className="text-slate-400 text-[11px] mt-0.5">
                    {formatDate(msg.createdAt)} ·{" "}
                    {STATUS_LABELS[msg.status] ?? msg.status}
                  </p>
                </div>
                <IconChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
